import { EditorState, NodeSelection, Transaction } from "prosemirror-state";

import { showFeatureModal } from "~/billing";
import type { AttendeeAttrs } from "~/common/Attendees";
import { displayActionItemLinkingModal } from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import {
	type ActionItemIntegration,
	type ActionItemIntegrationWithPanes,
	activeIntegrationLinks,
} from "~/integrations/actionItemIntegration/index";
// eslint-disable-next-line max-len
import { promptActionItemAlreadyConnected } from "~/integrations/actionItemIntegration/promptActionItemAlreadyConnected";
import { promptConnectingIntegrationModal } from "~/integrations/actionItemIntegration/promptConnectingIntegration";
import { selectionToJiraText } from "~/integrations/actionItemIntegration/utils";
import { createWorkspaceToast } from "~/lib/guestUserUtils";
import { getContext } from "~/lib/prose/plugins/context";
import type { TypeAheadState } from "~/lib/prose/plugins/typeahead";
import { expandSelectionToBlocks } from "~/lib/prose/utils";

import { asanaKey, displayAsanaActionItemLinkingModal } from "../asana";

export function slashCommandWrapper(integration: ActionItemIntegration) {
	return function command(_state: EditorState, typeAheadState?: TypeAheadState) {
		return (state: EditorState, _dispatch: (tr: Transaction) => void): boolean => {
			const context = getContext(state);
			const { userId, noteId } = context;
			const attendee: AttendeeAttrs | undefined = context?.participants?.find((u: AttendeeAttrs) => u.id === userId);

			let connectedActionItemIntegrations: string[] = [];
			if (context.connectedActionItemIntegrations)
				connectedActionItemIntegrations = connectedActionItemIntegrations.concat(
					context.connectedActionItemIntegrations,
				);
			if (context.asanaIsConnected) connectedActionItemIntegrations.push(asanaKey);

			if (attendee?.isGuest) {
				createWorkspaceToast({ closeOnClick: true, autoClose: 15000 });
				return false;
			}

			if (!context.billing?.featureAccess?.[integration.billingFeatureName]) {
				showFeatureModal(integration.billingFeatureName);
				return false;
			}
			if (!connectedActionItemIntegrations.includes(integration.key)) {
				promptConnectingIntegrationModal(integration);
				return false;
			}

			const tr = state.tr;
			const startPos = tr.selection.from;
			const [start] = expandSelectionToBlocks(startPos, startPos, tr.doc);
			const nodeSelection = NodeSelection.create(tr.doc, start);
			const blockKey = nodeSelection.node.attrs["blockId"];

			if (Object.values(activeIntegrationLinks({ position: start, state: state, blockKey: blockKey })).some(v => v)) {
				promptActionItemAlreadyConnected(integration);
				return false;
			}

			let initialText: string = selectionToJiraText(nodeSelection);

			if (typeAheadState) {
				const { matchingSelection } = typeAheadState;
				if (matchingSelection) {
					const mappedSelection = matchingSelection.resolve(tr.doc);
					mappedSelection.replace(tr);
					const currentSel = state.tr.selection;
					let modifiedTr = tr.setSelection(currentSel.map(tr.doc, tr.mapping));
					const modifiedNodeSelection = NodeSelection.create(modifiedTr.doc, start);

					initialText = selectionToJiraText(modifiedNodeSelection);
				}
			}

			if (integration.key === asanaKey) {
				displayAsanaActionItemLinkingModal({
					userId: userId,
					blockKey: blockKey,
					noteId: noteId,
					isAsanaConnected: context.asanaIsConnected ?? false,
					initialText: initialText,
				});
			} else {
				displayActionItemLinkingModal({
					integration: integration as ActionItemIntegrationWithPanes,
					userId,
					initialText,
					blockKey,
					noteId: noteId,
				});
			}
			return true;
		};
	};
}
